// KPI org configuration (spec §3.8, §3.9) — client mirror of server/utils/kpi-config-utils.js.
// Both run src/data-utils/__tests__/fixtures/kpi-config-cases.json (a byte copy of the server's
// tests/server/utils/fixtures/kpi-config-cases.json). The server stays the authority: this copy
// exists so Settings can validate before saving and label values the same way the API does.

export const NONE_VALUE = "__none__"
export const ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,47}$/
export const DIMENSION_KEY_PATTERN = /^[a-z][A-Za-z0-9]{0,31}$/
export const GROUP_SOURCES = Object.freeze(["exerciseTag", "member"])
export const INDIVIDUAL_RESULTS_MODES = Object.freeze(["named", "aggregatesOnly"])
export const RESERVED_DIMENSION_KEYS = Object.freeze([
  "department",
  "person",
  "month",
  "scenario",
  "lineage",
  "scenarioType",
  "setupType",
  "mode",
  "role",
  "nistFunction",
  "phase",
  "framework",
  "frameworkCategory",
])

export const SCENARIO_TYPE_LABELS = Object.freeze({ cyber: "Cyber", bcdr: "BC/DR", integrity: "Platform integrity", crisis: "Crisis" })
export const SETUP_TYPE_LABELS = Object.freeze({
  solo: "Solo",
  independentGroup: "Independent group",
  collaborative: "Collaborative",
  facilitated: "Facilitated",
})

const dept = (id, label) => Object.freeze({ id, label, archived: false })
export const DEFAULT_DEPARTMENTS = Object.freeze([
  dept("security-operations", "Security Operations"),
  dept("it-infrastructure", "IT Infrastructure"),
  dept("legal-compliance", "Legal & Compliance"),
  dept("communications", "Communications"),
  dept("executive-leadership", "Executive Leadership"),
  dept("facilities-operations", "Facilities & Operations"),
  dept("people-hr", "People (HR)"),
  dept("finance", "Finance"),
  dept("other", "Other"),
])

// Group dimensions (business units and other exercise/member tags) were retired 2026-10: departments
// are the only grouping. A stored org.groupDimensions is kept but ignored, so every org's effective
// dimensions are empty (mirrors the server's kpi-config-utils).
export const DEFAULT_GROUP_DIMENSIONS = Object.freeze([])

export const DEFAULT_KPI_PRIVACY = Object.freeze({ individualResults: "named", minGroupSize: 5, namesInExports: false, accessLogRetentionDays: 365 })
// KPI P2 §3.7: access-log retention offered in Settings (the validator allows 90–730).
export const ACCESS_LOG_RETENTION_OPTIONS = Object.freeze([90, 180, 365, 730])

export const effectiveDepartments = (org) => (Array.isArray(org?.departments) ? org.departments : DEFAULT_DEPARTMENTS)
export const effectiveGroupDimensions = () => DEFAULT_GROUP_DIMENSIONS
export const effectiveKpiPrivacy = (org) => ({
  ...DEFAULT_KPI_PRIVACY,
  ...(org?.kpiPrivacy && typeof org.kpiPrivacy === "object" ? org.kpiPrivacy : {}),
})

// Dimensions an exercise can be tagged with right now (wizard, spec §11.3).
export const taggableGroupDimensions = (groupDimensions) =>
  (Array.isArray(groupDimensions) ? groupDimensions : []).filter(
    (d) => Array.isArray(d?.sources) && d.sources.includes("exerciseTag") && (d.values ?? []).some((v) => v.archived !== true)
  )

const isPlainObject = (v) => v != null && typeof v === "object" && !Array.isArray(v)
const labelOk = ({ value, max }) => typeof value === "string" && value.trim().length >= 1 && value.trim().length <= max
const result = (errors) => ({ valid: errors.length === 0, errors })

export const validateDepartments = ({ departments }) => {
  if (!Array.isArray(departments) || departments.length < 1 || departments.length > 50) {
    return result(["departments must list 1 to 50 entries"])
  }
  const errors = []
  const ids = new Set()
  const activeLabels = new Set()
  for (const d of departments) {
    if (!isPlainObject(d)) {
      errors.push("each department must be an object")
      continue
    }
    if (typeof d.id !== "string" || !ID_PATTERN.test(d.id)) errors.push(`invalid department id '${d.id}'`)
    else if (ids.has(d.id)) errors.push(`duplicate department id '${d.id}'`)
    else ids.add(d.id)
    if (!labelOk({ value: d.label, max: 60 })) errors.push(`department '${d.id}' needs a label of 1-60 characters`)
    if (typeof d.archived !== "boolean") errors.push(`department '${d.id}' archived must be true or false`)
    if (d.id === "other" && d.archived === true) errors.push("'other' cannot be archived")
    if (d.archived !== true && labelOk({ value: d.label, max: 60 })) {
      const key = d.label.trim().toLowerCase()
      if (activeLabels.has(key)) errors.push(`duplicate department label '${d.label.trim()}'`)
      activeLabels.add(key)
    }
  }
  if (!ids.has("other")) errors.push("the 'other' department must stay in the list")
  return result(errors)
}

export const validateGroupDimensions = ({ groupDimensions }) => {
  if (!Array.isArray(groupDimensions) || groupDimensions.length > 10) return result(["groupDimensions must list at most 10 dimensions"])
  const errors = []
  const keys = new Set()
  const allValueIds = new Set()
  const parentRefs = []
  for (const dim of groupDimensions) {
    if (!isPlainObject(dim)) {
      errors.push("each dimension must be an object")
      continue
    }
    if (typeof dim.key !== "string" || !DIMENSION_KEY_PATTERN.test(dim.key)) errors.push(`invalid dimension key '${dim.key}'`)
    else if (RESERVED_DIMENSION_KEYS.includes(dim.key)) errors.push(`'${dim.key}' is a reserved name`)
    else if (keys.has(dim.key)) errors.push(`duplicate dimension key '${dim.key}'`)
    else keys.add(dim.key)
    if (!labelOk({ value: dim.label, max: 40 })) errors.push(`dimension '${dim.key}' needs a label of 1-40 characters`)
    if (!Array.isArray(dim.sources) || dim.sources.length === 0 || dim.sources.some((s) => !GROUP_SOURCES.includes(s))) {
      errors.push(`dimension '${dim.key}' sources must be a non-empty subset of ${GROUP_SOURCES.join(", ")}`)
    }
    if (typeof dim.requiredOnCreate !== "boolean") errors.push(`dimension '${dim.key}' requiredOnCreate must be true or false`)
    if (!Array.isArray(dim.values) || dim.values.length > 200) {
      errors.push(`dimension '${dim.key}' may have at most 200 values`)
      continue
    }
    const ids = new Set()
    const labels = new Set()
    for (const v of dim.values) {
      if (!isPlainObject(v)) {
        errors.push(`dimension '${dim.key}' values must be objects`)
        continue
      }
      if (typeof v.id !== "string" || !ID_PATTERN.test(v.id)) errors.push(`invalid value id '${v.id}' in '${dim.key}'`)
      else if (ids.has(v.id)) errors.push(`duplicate value id '${v.id}' in '${dim.key}'`)
      else {
        ids.add(v.id)
        allValueIds.add(v.id)
      }
      if (!labelOk({ value: v.label, max: 60 })) errors.push(`value '${v.id}' in '${dim.key}' needs a label of 1-60 characters`)
      else {
        const key = v.label.trim().toLowerCase()
        if (labels.has(key)) errors.push(`duplicate value label '${v.label.trim()}' in '${dim.key}'`)
        labels.add(key)
      }
      if (typeof v.archived !== "boolean") errors.push(`value '${v.id}' archived must be true or false`)
      if (v.parentValueId !== null && v.parentValueId !== undefined) {
        if (typeof v.parentValueId !== "string") errors.push(`value '${v.id}' parentValueId must be a value id or null`)
        else parentRefs.push({ id: v.id, parentValueId: v.parentValueId })
      }
    }
  }
  for (const ref of parentRefs) {
    if (!allValueIds.has(ref.parentValueId)) errors.push(`value '${ref.id}' parentValueId '${ref.parentValueId}' does not exist`)
  }
  return result(errors)
}

export const validateKpiPrivacy = ({ kpiPrivacy }) => {
  if (!isPlainObject(kpiPrivacy)) return result(["kpiPrivacy must be an object"])
  const errors = []
  const allowed = ["individualResults", "minGroupSize", "namesInExports", "accessLogRetentionDays"]
  for (const key of Object.keys(kpiPrivacy)) if (!allowed.includes(key)) errors.push(`unknown kpiPrivacy field '${key}'`)
  if (!INDIVIDUAL_RESULTS_MODES.includes(kpiPrivacy.individualResults)) errors.push("individualResults must be 'named' or 'aggregatesOnly'")
  if (!Number.isInteger(kpiPrivacy.minGroupSize) || kpiPrivacy.minGroupSize < 3 || kpiPrivacy.minGroupSize > 25)
    errors.push("minGroupSize must be an integer from 3 to 25")
  if (typeof kpiPrivacy.namesInExports !== "boolean") errors.push("namesInExports must be true or false")
  // Optional on write: an omitted value keeps the stored one (server mirror).
  const days = kpiPrivacy.accessLogRetentionDays
  if (days !== undefined && (!Number.isInteger(days) || days < 90 || days > 730)) errors.push("accessLogRetentionDays must be an integer from 90 to 730")
  return result(errors)
}

export const validateGroupTags = ({ groupTags, groupDimensions, previousGroupTags = {}, isCreate = false }) => {
  if (groupTags == null) return { valid: true, errors: [], value: {} }
  if (!isPlainObject(groupTags)) return { valid: false, errors: ["groupTags must be an object"], value: {} }
  const errors = []
  const value = {}
  const dims = Array.isArray(groupDimensions) ? groupDimensions : []
  const entries = Object.entries(groupTags)
  if (entries.length > 10) errors.push("groupTags may have at most 10 keys")
  for (const [key, raw] of entries) {
    if (raw === null || raw === "") continue
    const dim = dims.find((d) => d.key === key)
    if (!dim) {
      errors.push(`unknown group '${key}'`)
      continue
    }
    if (!Array.isArray(dim.sources) || !dim.sources.includes("exerciseTag")) {
      errors.push(`group '${key}' is not set on exercises`)
      continue
    }
    const found = (dim.values ?? []).find((v) => v.id === raw)
    if (!found) {
      errors.push(`unknown value '${raw}' for group '${key}'`)
      continue
    }
    if (found.archived === true && previousGroupTags?.[key] !== raw) {
      errors.push(`value '${raw}' for group '${key}' is archived`)
      continue
    }
    value[key] = raw
  }
  if (isCreate) {
    for (const dim of dims) {
      const selectable = (dim.values ?? []).some((v) => v.archived !== true)
      if (dim.requiredOnCreate === true && dim.sources?.includes("exerciseTag") && selectable && !value[dim.key]) {
        errors.push(`${dim.label} is required`)
      }
    }
  }
  return { valid: errors.length === 0, errors, value }
}

export const departmentIdsRemoved = ({ before, after }) => {
  const kept = new Set((after ?? []).map((d) => d.id))
  return (before ?? []).map((d) => d.id).filter((id) => !kept.has(id))
}

export const departmentIdsNewlyArchived = ({ before, after }) => {
  const wasActive = new Set((before ?? []).filter((d) => d.archived !== true).map((d) => d.id))
  return (after ?? []).filter((d) => d.archived === true && wasActive.has(d.id)).map((d) => d.id)
}

export const groupValuesRemoved = ({ before, after }) => {
  const out = []
  for (const dim of before ?? []) {
    const next = (after ?? []).find((d) => d.key === dim.key)
    const kept = new Set((next?.values ?? []).map((v) => v.id))
    for (const v of dim.values ?? []) if (!kept.has(v.id)) out.push({ key: dim.key, id: v.id })
  }
  return out
}

export const departmentLabel = ({ departments, id }) => {
  if (id === NONE_VALUE || id == null) return "No department"
  const found = (departments ?? []).find((d) => d.id === id)
  if (!found) return "Unknown"
  return found.archived ? `${found.label} (archived)` : found.label
}

export const groupValueLabel = ({ groupDimensions, key, id }) => {
  const dim = (groupDimensions ?? []).find((d) => d.key === key)
  if (id === NONE_VALUE || id == null) return `No ${(dim?.label ?? "group").toLowerCase()}`
  const found = (dim?.values ?? []).find((v) => v.id === id)
  if (!found) return "Unknown"
  return found.archived ? `${found.label} (archived)` : found.label
}

// ── ORG_MEMBER profile (KPI P2 §3.5) — the client mirror of the server's validateMemberProfile ──
const MEMBER_EMAIL = /^[^\s@]{1,64}@[^\s@]{1,190}\.[^\s@]{1,63}$/
export const MEMBER_TITLE_MAX = 80
export const MEMBER_ALIASES_MAX = 10
export const memberGroupDimensions = (groupDimensions) => (groupDimensions ?? []).filter((d) => (d.sources ?? []).includes("member"))

export const validateMemberProfile = ({ profile, departments, groupDimensions, previous = null }) => {
  const errors = []
  const value = {}
  const email = typeof profile?.email === "string" ? profile.email.trim().toLowerCase() : ""
  if (!MEMBER_EMAIL.test(email)) errors.push("email must be a valid email address")
  value.email = email
  if ("title" in profile) {
    const title = profile.title == null ? "" : String(profile.title).trim().replace(/\s+/g, " ")
    if (title.length > MEMBER_TITLE_MAX) errors.push(`title must be at most ${MEMBER_TITLE_MAX} characters`)
    value.title = title || null
  }
  if ("departmentId" in profile) {
    const id = profile.departmentId || null
    const dept = id ? (departments ?? []).find((d) => d.id === id) : null
    if (id && !dept) errors.push(`unknown department '${id}'`)
    else if (dept?.archived && previous?.departmentId !== id) errors.push(`department '${dept.label}' is archived`)
    value.departmentId = id
  }
  if ("groups" in profile) {
    value.groups = {}
    for (const [key, raw] of Object.entries(profile.groups ?? {})) {
      const dim = (groupDimensions ?? []).find((d) => d.key === key)
      if (!dim || !(dim.sources ?? []).includes("member")) {
        errors.push(`'${key}' is not a member group dimension`)
        continue
      }
      const id = raw || null
      const v = id ? (dim.values ?? []).find((x) => x.id === id) : null
      if (id && !v) errors.push(`unknown ${dim.label} value '${id}'`)
      else if (v?.archived && previous?.groups?.[key] !== id) errors.push(`${dim.label} value '${v.label}' is archived`)
      value.groups[key] = id
    }
  }
  if ("managerEmail" in profile) {
    const manager = profile.managerEmail == null ? "" : String(profile.managerEmail).trim().toLowerCase()
    if (manager && !MEMBER_EMAIL.test(manager)) errors.push("managerEmail must be a valid email address")
    if (manager && manager === email) errors.push("a member cannot be their own manager")
    value.managerEmail = manager || null
  }
  if ("aliases" in profile) {
    const aliases = [...new Set((profile.aliases ?? []).map((a) => String(a ?? "").trim().toLowerCase()).filter(Boolean))]
    if (aliases.length > MEMBER_ALIASES_MAX) errors.push(`at most ${MEMBER_ALIASES_MAX} aliases`)
    if (aliases.some((a) => !MEMBER_EMAIL.test(a))) errors.push("every alias must be a valid email address")
    if (aliases.includes(email)) errors.push("an alias cannot be the member’s own email")
    value.aliases = aliases
  }
  return { valid: errors.length === 0, errors, value }
}
